import './ChatResponse.scss';
import React from 'react';
import DOMPurify from 'dompurify';
import { MonitorType } from '../../../../../../../shared/types/monitor/MonitorType';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { getMonitorTypeIcon } from '../../helpers/StatsHelper';
import { getMonitorFullName } from '../../../../../../../shared/helpers/MonitorTypeHelpers';
import { v4 as uuidv4 } from 'uuid';
import CustomTooltip from '../../../../../../../shared/components/common/tooltip/CustomTooltip';

/**
 * Odpowiedzi modeli API idą przez `rehypeRaw`, bo `processedContent` wstrzykuje do markdownu
 * `<span data-chatbeat-keyword>`. Tą samą furtką wchodził surowy HTML od modelu — a prompt injection
 * w pytaniu potrafi zmusić model do wypisania `<iframe>`/`<script>`. Allowlist zostawia znaczniki
 * markdownu i nasz span; reszta (iframe, script, style, on*, javascript:) wypada.
 */
const ANSWER_SANITIZE_SCHEMA = {
  ...defaultSchema,
  attributes: { ...defaultSchema.attributes, span: ['dataChatbeatKeyword'] },
  // Domyślnie z treścią znika tylko <script>; <style> zostawiał swój CSS jako goły tekst.
  strip: [...(defaultSchema.strip ?? []), 'style'],
};

interface ChatResponseProps {
  prompt: string;
  currentPosition: number;
  created: Date;
  content: string;
  organicContent: string;
  htmlContent: string;
  monitorType: MonitorType;
  keyword: string;
  /** Wszystkie nazwy marki (warianty, dołączone marki) — podświetlamy każdą; brak = sam `keyword`. */
  brandNames?: string[];
  positions: { item: string; position: number }[];
  /** Optional slot rendered in the right column below positions (V2 ad card). */
  adSlot?: React.ReactNode;
  /** Optional slot rendered in the right column below positions/ads (V2 shopping cards). */
  productsSlot?: React.ReactNode;
  /**
   * Remove ChatGPT's inline "sponsored" ad units (OpenAI bazaar) from the answer body. V2 surfaces
   * those ads in the right column via `adSlot`, so the inline duplicate is stripped. Defaults to
   * false to keep the classic V1 view untouched.
   */
  stripInlineAds?: boolean;
}

const ChatResponse: React.FC<ChatResponseProps> = ({
  prompt,
  currentPosition,
  created,
  content,
  organicContent,
  htmlContent,
  monitorType,
  keyword,
  brandNames,
  positions,
  adSlot,
  productsSlot,
  stripInlineAds = false,
}) => {
  const { t } = useTranslation();

  const parser = new DOMParser();

  const htmlSupportedModels = [
    'GEMINI_SEARCH_BROWSER',
    'PERPLEXITY_SEARCH_BROWSER',
    'GROK_SEARCH_BROWSER',
    'COPILOT_SEARCH_BROWSER',
    'GPT_SEARCH_BROWSER',
    'AI_MODE',
  ];

  const isAPIModel = [
    'GPT',
    'GPT_API',
    'GPT_API_BROWSER',
    'GPT_API_SEARCH_WEB',
    'GEMINI_API',
    'GEMINI_API_BROWSER',
    'CLAUDE_API',
    'DEEP_SEEK_API',
    'PERPLEXITY_API',
    'AI_OVERVIEW_NEW',
  ].includes(monitorType);

  const getMainSelector = (monitorType: MonitorType) => {
    switch (monitorType) {
      case 'GEMINI_SEARCH_BROWSER':
        return 'message-content';

      // Perplexity's current layout renders the answer into div[data-renderer="lm"]; the older one
      // used div#markdown-content-0. Without the second selector the extraction falls back to
      // <body> and renders the whole page (sidebar, sources rail, follow-up composer).
      case 'PERPLEXITY_SEARCH_BROWSER':
        return 'div#markdown-content-0, div[data-renderer="lm"]';

      case 'COPILOT_SEARCH_BROWSER':
        return 'div[data-content]';

      case 'GROK_SEARCH_BROWSER':
        return '#last-reply-container';

      // ChatGPT's logged-out/mobile layout wraps each turn in <li data-message-role>; the classic
      // layout used div[data-message-author-role]. Without the second selector the extraction falls
      // back to <body> and renders the whole page (sidebar, login modal, settings). Must stay a
      // FIRST match — a snapshot's later assistant turns are our own entity-extraction prompts.
      case 'GPT_SEARCH_BROWSER':
        return 'div[data-message-author-role="assistant"], li[data-message-role="assistant"]';

      case 'AI_MODE':
        return 'div[data-container-id="main-col"]';

      default:
        return '';
    }
  };

  /**
   * Żółte podświetlenie znaczy „rozpoznaliśmy tę markę w tej odpowiedzi", a nie „ta fraza tu jest".
   * Bez tego warunku odpowiedź, w której ekstrakcja marki NIE znalazła, i tak świeciła na żółto —
   * i rozjeżdżała się z kolumną Positions obok, gdzie marki nie było.
   */
  const recognizedNames = (brandNames?.length ? brandNames : [keyword])
    .map((name) => name.trim().toLowerCase())
    .filter((name) => positions.some((p) => p.item.trim().toLowerCase() === name));
  const isKeywordRecognized = recognizedNames.length > 0;
  // Dłuższe nazwy najpierw — „ania kruk jewellery” ma wygrać z „ania kruk” w tym samym miejscu.
  const namesPattern = [...recognizedNames]
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');

  const highlightKeyword = (children: any): any => {
    if (typeof children === 'string' && isKeywordRecognized) {
      const regex = new RegExp(`(^|[\\s(\\["])(${namesPattern})([\\s)\\]".,!?;:-]|$)`, 'gi');
      const parts = children.split(regex);
      if (parts.length <= 1) {
        return children;
      }

      return parts.map((part, index) => {
        if (recognizedNames.includes(part.trim().toLowerCase())) {
          return (
            <span key={index} data-chatbeat-keyword>
              {part}
            </span>
          );
        }
        return part;
      });
    }

    if (Array.isArray(children)) {
      return children.map((child, index) => (
        <React.Fragment key={index}>{highlightKeyword(child)}</React.Fragment>
      ));
    }

    return children;
  };

  const wrapKeywordInSpan = (element: Element, doc: Document) => {
    if (!isKeywordRecognized) {
      return;
    }
    const regex = new RegExp(`(^|\\s)(${namesPattern})(\\s|$|-|:|,|;|.)`, 'gi');

    const processNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';

        if (regex.test(text)) {
          const newHTML = text.replace(regex, '$1<span data-chatbeat-keyword>$2</span>$3');

          const tempDiv = doc.createElement('div');
          tempDiv.innerHTML = newHTML;

          const parent = node.parentNode;
          while (tempDiv.firstChild) {
            parent?.insertBefore(tempDiv.firstChild, node);
          }
          parent?.removeChild(node);
        }
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'A') {
        return;
      }

      Array.from(node.childNodes).forEach(processNode);
    };

    processNode(element);
  };
  const processedContent = (content: string) =>
    !isKeywordRecognized
      ? content
      : content.replace(
          new RegExp(`(^|\\s)(${namesPattern})(\\s|$|-|:|,|;|.)`, 'gi'),
          '$1<span data-chatbeat-keyword>$2</span>$3'
        );

  const modelName = `tooltip-${uuidv4()}-${monitorType}`;

  const hasPositions = positions.length > 0;
  const hasSideColumn = hasPositions || !!adSlot || !!productsSlot;

  // Some models (e.g. AI Overview) occasionally return nothing — detect it so we can tell the user,
  // instead of rendering the question with a blank answer below.
  const htmlHasText = (html: string): boolean => {
    if (!html?.trim()) return false;
    return Boolean(
      parser.parseFromString(html, 'text/html').querySelector('body')?.textContent?.trim()
    );
  };
  // Browser types render the scraped page HTML — but only when there is one. The same type can also
  // be answered by a direct API (Copilot via the Copilot Chat API) that stores markdown in `content`
  // and no HTML; judging emptiness by the HTML alone showed "no answer" for a complete answer.
  const isHtmlModel = htmlSupportedModels.includes(monitorType) && htmlHasText(htmlContent);
  const isEmptyResponse = isHtmlModel
    ? !htmlHasText(htmlContent)
    : !content?.trim() && !organicContent?.trim();

  return (
    <div className={`chat-response${!hasSideColumn ? ' chat-response--full-width' : ''}`}>
      <div className="chat-response__message chat-message">
        <div className="chat-message__box">
          <div className="chat-message__content">
            <div className="chat-message__header">
              <div className="chat-message__position">
                {monitorType && (
                  <div className="chat-message__type">
                    <span id={modelName} className={`${getMonitorTypeIcon(monitorType)} ico--md`} />
                    {getMonitorFullName(monitorType, created)}
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="chat-message__cloud-question">{prompt}</div>
            </div>
            {isEmptyResponse ? (
              <div className="chat-message__cloud chat-message__cloud--empty">
                <span className="ico ico--info ico--xxs" />
                {t('noModelResponse')}
              </div>
            ) : isHtmlModel ? (
              <div
                className="chat-message__cloud"
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    const doc = parser.parseFromString(htmlContent, 'text/html');
                    const body = doc.querySelector('body');
                    const mainSelector = body?.querySelector(getMainSelector(monitorType));

                    if (!body) {
                      return '';
                    }

                    const hasMainSelector = Boolean(mainSelector && mainSelector.textContent?.trim());
                    const messageSelector = hasMainSelector ? mainSelector : body;

                    if (messageSelector) {
                      const isValidUrl = (str: string): boolean => {
                        if (
                          !str ||
                          str.length < 4 ||
                          /^(button|false|true|null|undefined)$/i.test(str)
                        ) {
                          return false;
                        }

                        try {
                          const urlString =
                            str.startsWith('http://') || str.startsWith('https://')
                              ? str
                              : `https://${str}`;
                          const url = new URL(urlString);

                          return (
                            (url.protocol === 'http:' || url.protocol === 'https:') &&
                            url.hostname.includes('.')
                          );
                        } catch {
                          return false;
                        }
                      };

                      const extractUrlFromButton = (button: Element): string | null => {
                        const attributes = Array.from(button.attributes);
                        for (const attr of attributes) {
                          if (isValidUrl(attr.value)) {
                            return attr.value.startsWith('http') ? attr.value : `https://${attr.value}`;
                          }
                        }

                        const text = button.textContent?.trim() || '';

                        const urlRegex =
                          /(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?/g;
                        const matches = text.match(urlRegex);

                        if (matches) {
                          for (const match of matches) {
                            if (isValidUrl(match)) {
                              return match.startsWith('http') ? match : `https://${match}`;
                            }
                          }
                        }

                        return null;
                      };

                      messageSelector.querySelectorAll('button span').forEach((span) => {
                        if (!span.textContent?.trim() || span.textContent.includes('+')) {
                          span.remove();
                        }
                      });

                      messageSelector.querySelectorAll('button').forEach((button) => {
                        const url = extractUrlFromButton(button);
                        const buttonText = button.textContent?.trim().toLowerCase() || '';
                        const containsKeyword = buttonText.includes(keyword.toLowerCase());
                        const hasAutofocus = button.hasAttribute('data-spatial-navigation-autofocus');
                        const isEmpty = !button.textContent?.trim();

                        if (url) {
                          const link = doc.createElement('a');
                          link.href = url;
                          link.textContent = button.textContent?.trim() || url;
                          link.target = '_blank';
                          link.rel = 'noopener noreferrer';

                          button.parentNode?.replaceChild(link, button);
                        } else if (containsKeyword && !isEmpty && !hasAutofocus) {
                          const span = doc.createElement('span');
                          span.textContent = button.textContent?.trim() || '';

                          button.parentNode?.replaceChild(span, button);
                        } else {
                          button.remove();
                        }
                      });

                      // Static map embeds (MapLibre/TomTom, Google Maps) can't initialize without
                      // JS in our sanitized render — they collapse to a large empty box with only
                      // leftover attribution/controls. Remove the whole map card (its nearest
                      // fixed-height ancestor) so the answer doesn't show a blank gap.
                      messageSelector
                        .querySelectorAll('[class*="maplibregl-map"], [class*="gm-style"], gmp-map')
                        .forEach((mapEl) => {
                          let target: Element = mapEl;
                          let ancestor: Element | null = mapEl.parentElement;
                          for (let i = 0; i < 5 && ancestor && ancestor !== messageSelector; i++) {
                            const cls = ancestor.getAttribute('class') || '';
                            const style = ancestor.getAttribute('style') || '';
                            if (/\bh-(\[|\d|full|screen)/.test(cls) || /height\s*:/.test(style)) {
                              target = ancestor;
                              break;
                            }
                            ancestor = ancestor.parentElement;
                          }
                          target.remove();
                        });

                      // Strip ChatGPT's inline sponsored ad units (OpenAI "bazaar") — V2 shows the
                      // same ad in the answer's right column (AnswerAdSlot), so drop the inline copy.
                      if (stripInlineAds) {
                        messageSelector
                          .querySelectorAll(
                            '[data-testid="bazaar-v2-elevated-box"], [data-ad-card-root="true"]'
                          )
                          .forEach((adEl) => adEl.remove());
                      }

                      messageSelector.querySelectorAll('span, sup, div, p ').forEach((div) => {
                        const hasUserMessage = div.getAttribute('data-content') === 'user-message'; // COPILOT
                        const hasCitationCard = div.getAttribute('data-testid') === 'citation-cards-row'; // COPILOT
                        const hasDateDivider = div.getAttribute('data-testid') === 'date-divider'; // COPILOT
                        const hasNoOpener = div.getAttribute('rel') === 'noopener'; // COPILOT
                        const hasActionButtons = div.getAttribute('class')?.includes('action-buttons'); //GROK
                        const hasImageViewer = div.getAttribute('data-testid') === 'image-viewer'; //GROK
                        const hasXId = div.getAttribute('data-xid') === 'Gd7Hsc'; //AI_MODE

                        if (
                          !div.textContent?.trim() ||
                          hasUserMessage ||
                          hasCitationCard ||
                          hasDateDivider ||
                          hasNoOpener ||
                          hasActionButtons ||
                          hasImageViewer ||
                          hasXId
                        ) {
                          div.remove();
                        }
                      });

                      const highlightedChats = messageSelector.querySelector(
                        'div[data-testid="highlighted-chats"]'
                      );

                      if (highlightedChats) {
                        // Copilot's turn labels ("You said" / "Copilot said"). They sit directly under
                        // the turn wrapper — headings from the answer body are nested deeper, so they
                        // survive. Older snapshots carried aria-label here, newer ones don't.
                        const headings = ':is(h1, h2, h3, h4, h5, h6)';
                        highlightedChats
                          .querySelectorAll(
                            `div > div[data-tabster] > div > ${headings}, ` +
                              `div[data-content="ai-message"] > div > ${headings}`
                          )
                          .forEach((header) => header.remove());
                      }

                      wrapKeywordInSpan(messageSelector, doc);
                    }

                    return DOMPurify.sanitize(messageSelector?.innerHTML || '', {
                      // iframe/object/embed DOMPurify wycina domyślnie — wpisane jawnie, żeby
                      // podniesienie wersji albo ADD_TAGS tego nie otworzyło.
                      FORBID_TAGS: [
                        'iframe',
                        'object',
                        'embed',
                        'form',
                        'style',
                        'script',
                        'meta',
                        'html',
                        'head',
                        'body',
                        'textarea',
                        'input',
                        'svg',
                        'dialog',
                      ],
                      FORBID_ATTR: [
                        'style',
                        'class',
                        'id',
                        'data-path-to-node',
                        'aria-expanded',
                        'tabindex',
                        'data-copy',
                        'role',
                        'data-tabster-dummy',
                      ],
                    });
                  })(),
                }}
              />
            ) : (
              <>
                <ReactMarkdown
                  className="chat-message__cloud"
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, [rehypeSanitize, ANSWER_SANITIZE_SCHEMA]]}
                  components={{
                    strong: ({ children }) => <strong>{highlightKeyword(children)}</strong>,
                    em: ({ children }) => <em>{highlightKeyword(children)}</em>,
                    p: ({ children }) => <p>{highlightKeyword(children)}</p>,
                    span: ({ children }) => <span>{highlightKeyword(children)}</span>,
                    li: ({ children }) => <li>{highlightKeyword(children)}</li>,
                  }}
                >
                  {organicContent ? processedContent(organicContent) : processedContent(content)}
                </ReactMarkdown>
                {organicContent && organicContent.trim() !== '' && (
                  <>
                    <div>
                      <div className="chat-message__cloud-question chat-message__cloud-question--info">
                        {t('expandOrganicAnswer')}
                        <CustomTooltip tooltipText="expandedInfo" addMargin={false} />
                      </div>
                    </div>
                    <ReactMarkdown
                      className="chat-message__cloud"
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw, [rehypeSanitize, ANSWER_SANITIZE_SCHEMA]]}
                      components={{
                        strong: ({ children }) => <strong>{highlightKeyword(children)}</strong>,
                        em: ({ children }) => <em>{highlightKeyword(children)}</em>,
                        p: ({ children }) => <p>{highlightKeyword(children)}</p>,
                        span: ({ children }) => <span>{highlightKeyword(children)}</span>,
                        li: ({ children }) => <li>{highlightKeyword(children)}</li>,
                      }}
                    >
                      {processedContent(content)}
                    </ReactMarkdown>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {hasSideColumn && (
        <div className="chat-response__stats">
          {hasPositions && (
            <div className="chat-info-box">
              <div className="chat-info-box__header">{t('positions')}</div>
              <div className="chat-info-box__content-box">
                {positions.map(({ item, position }) => (
                  <div
                    className={`chat-info-box-item ${
                      position === currentPosition ? 'chat-info-box-item--selected' : ''
                    }`}
                    key={`${item}-${position}`}
                  >
                    <div className="chat-info-box-item__position">{position}</div>
                    <span className="capitalize">{item}</span>
                  </div>
                ))}
                {organicContent && (
                  <div className="chat-info-box__info">
                    <span className="ico ico--info ico--xxs"></span>
                    <div>{t('expandedInfo')}</div>
                  </div>
                )}
              </div>
            </div>
          )}
          {adSlot}
          {productsSlot}
        </div>
      )}
    </div>
  );
};

export default ChatResponse;
